'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import TestimonialsSection from '@/components/TestimonialsSection';
import { Target, Shield, Compass, Users, Grid, RefreshCw, Globe, ArrowUpRight } from 'lucide-react';

// Custom Count-Up Counter Component for stats
function Counter({
  value,
  label,
  suffix = '+',
}: {
  value: number;
  label: string;
  suffix?: string;
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = value;
    if (start === end) return;

    const totalDuration = 2000;
    const incrementTime = Math.max(Math.floor(totalDuration / end), 12);

    const timer = setInterval(() => {
      start += Math.ceil(end / (totalDuration / incrementTime));
      if (start >= end) {
        clearInterval(timer);
        setCount(end);
      } else {
        setCount(start);
      }
    }, incrementTime);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <div className="flex flex-col items-center justify-center p-6 bg-white border border-slate-100 hover:border-slate-200 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.03)] hover:shadow-[0_10px_30px_-5px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 group">
      <span className="text-4xl md:text-5xl font-black text-[#006838] tracking-tight mb-2 transition-transform duration-300 group-hover:scale-105">
        {count}
        <span className="text-3xl font-extrabold text-[#82BC41] ml-0.5">{suffix}</span>
      </span>
      <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest text-center leading-relaxed max-w-[150px]">
        {label}
      </span>
    </div>
  );
}

interface AboutTemplateProps {
  page?: any;
  meta?: any;
  slug?: string;
}

export default function AboutTemplate({ page, meta, slug }: AboutTemplateProps) {
  // Use metadata fields with robust defaults
  const pageTitle = page?.title || 'About Us';
  const bannerImage = page?.featured_image || '/uploads/about-dubai-skyline.jpg';

  // Custom stats parsing
  const stat1Val = Number(meta?.stat1_val) || 15;
  const stat1Lbl = meta?.stat1_lbl || 'Years Real Estate Experience';
  const stat2Val = Number(meta?.stat2_val) || 130;
  const stat2Lbl = meta?.stat2_lbl || 'Agent Network';
  const stat3Val = Number(meta?.stat3_val) || 100;
  const stat3Lbl = meta?.stat3_lbl || 'Developers Connected';
  const stat4Val = Number(meta?.stat4_val) || 178;
  const stat4Lbl = meta?.stat4_lbl || 'Verified Land Listings';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Jost, sans-serif' }}>
      <Header />

      {/* 1. Page Hero Banner & Breadcrumbs */}
      <div className="pt-[110px] pb-10 bg-slate-50 border-b border-slate-100/80">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1
                className="text-3xl md:text-4xl font-black text-[#1a1a1a] uppercase tracking-tight"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {pageTitle}
              </h1>
              {/* Green underline accent */}
              <div className="w-16 h-1 bg-[#006838] mt-2"></div>
            </div>

            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400">
              <Link href="/" className="hover:text-[#006838] transition-colors">
                Home
              </Link>
              <span>/</span>
              <span className="text-slate-600 font-extrabold">{pageTitle}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. B2B Introduction Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content (60%) */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                {meta?.intro_subtitle || "UAE's Premier B2B Land Gateway"}
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] leading-tight uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {meta?.intro_title || 'PlotAE: Your destination for verified land listings'}
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>

              {page?.content ? (
                <div
                  className="text-slate-600 text-base leading-relaxed font-medium space-y-4"
                  dangerouslySetInnerHTML={{ __html: page.content }}
                />
              ) : (
                <div className="text-slate-600 text-base leading-relaxed font-medium space-y-4">
                  <p>
                    PlotAE is the UAE’s first exclusive B2B marketplace focused solely on the buying
                    and selling of verified land parcels and plots. As an initiative of Bizmaker
                    Investments and Real Estate, PlotAE is backed by over 15 years of in-depth
                    experience in the UAE’s real estate sector. At PlotAE, we’re building the most
                    trusted land marketplace, dedicated exclusively to one high-potential asset
                    class: land.
                  </p>
                  <p>
                    Unlike general property portals, PlotAE offers verified land listings, built
                    specifically for developers, landowners, brokers, and institutional
                    investors—all actively engaged in land transactions. We simplify and accelerate
                    the process of land acquisition and sales by ensuring transparent listings,
                    verified data, and a streamlined B2B transaction flow across the UAE.
                  </p>
                </div>
              )}

              <div className="pt-4">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-[#006838] text-white font-black text-xs uppercase tracking-widest px-8 py-4 shadow-lg hover:bg-[#82BC41] transition-all duration-300 rounded-none group hover:scale-[1.02]"
                >
                  Contact us
                  <ArrowUpRight
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </Link>
              </div>
            </div>

            {/* Right Panoramic Image (40%) */}
            <div className="lg:col-span-5">
              <div className="relative aspect-[4/3] w-full bg-slate-100 shadow-[0_15px_35px_rgba(0,0,0,0.06)] border border-slate-100 overflow-hidden group">
                <div className="absolute inset-0 bg-[#006838]/5 mix-blend-multiply z-10"></div>
                <Image
                  src={bannerImage}
                  alt={pageTitle}
                  fill
                  className="object-cover transition-transform duration-[8000ms] ease-out group-hover:scale-105"
                  sizes="(max-width: 1024px) 100vw, 40vw"
                  priority
                />
                {/* Decorative Accent Lines */}
                <div className="absolute bottom-4 right-4 w-12 h-12 border-r-2 border-b-2 border-white/60 z-20 transition-all duration-300 group-hover:scale-110"></div>
                <div className="absolute top-4 left-4 w-12 h-12 border-l-2 border-t-2 border-white/60 z-20 transition-all duration-300 group-hover:scale-110"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Investment Intelligence & Deal Score */}
      <section className="py-20 bg-slate-50 border-y border-slate-100">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Image Column (40%) */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative aspect-[4/3] w-full bg-slate-100 shadow-[0_15px_35px_rgba(0,0,0,0.06)] border border-slate-150 overflow-hidden group">
                <Image
                  src={meta?.intelligence_image || '/uploads/about-unnamed-1.jpg'}
                  alt="Investment Intelligence Dashboard"
                  fill
                  className="object-cover transition-transform duration-[8000ms] ease-out group-hover:scale-105"
                  sizes="(max-width: 1024px) 100vw, 40vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#006838]/20 via-transparent to-transparent z-10"></div>
                <div className="absolute bottom-4 left-4 w-12 h-12 border-l-2 border-b-2 border-white/60 z-20 transition-all duration-300 group-hover:scale-110"></div>
                <div className="absolute top-4 right-4 w-12 h-12 border-r-2 border-t-2 border-white/60 z-20 transition-all duration-300 group-hover:scale-110"></div>
              </div>
            </div>

            {/* Right Content Column (60%) */}
            <div className="lg:col-span-7 space-y-6 order-1 lg:order-2">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                {meta?.intelligence_subtitle || 'Exclusive Data & Rating Tech'}
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] leading-tight uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {meta?.intelligence_title || 'Investment Intelligence'}
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>

              <p className="text-slate-600 text-base leading-relaxed font-medium">
                {meta?.intelligence_desc ||
                  'PlotAE is not just a listing site. It’s an investment intelligence platform that transforms raw real estate data into clear, actionable decisions. Powered by advanced analytics, PlotAE decodes past transactions, price trends, demographic shifts, and community growth to reveal the real story behind every land parcel.'}
              </p>

              <div className="bg-white p-6 border-l-4 border-[#006838] shadow-[0_4px_20px_rgba(0,0,0,0.02)] space-y-3">
                <h4
                  className="text-sm font-black uppercase tracking-wider text-[#006838]"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {meta?.deal_score_title || 'Our Proprietary Deal Score Engine'}
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed font-medium">
                  {meta?.deal_score_desc ||
                    'Our Data Intelligence dashboards empower investors, developers, and brokers to assess ROI, track market performance, and pinpoint the best buy-or-sell moments with precision. What truly differentiates us is our proprietary Deal Score, a smart algorithm that rates plots by location, price accuracy, growth potential, legal transparency, and demand giving you data-backed clarity before every move.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Why Choose PlotAE Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
              Market-Leading Advantages
            </span>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Why Choose PlotAE ?
            </h2>
            <div className="w-16 h-1 bg-[#82BC41] mx-auto rounded"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column Description (50%) */}
            <div className="lg:col-span-6 space-y-6">
              <div className="text-slate-700 text-lg font-bold leading-relaxed">
                At PlotAE, we believe land is the foundation of all real estate value.
              </div>

              <p className="text-slate-600 text-base leading-relaxed font-medium">
                That’s why we’ve built a verified plot marketplace in the UAE, where developers,
                agents, and investors can access the right tools, actionable insights, and a trusted
                network to support their objectives.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link
                  href="/luxury-properties"
                  className="flex-1 border-b-2 border-slate-100 hover:border-[#006838] bg-slate-50 p-4 transition-all duration-300 flex items-center justify-between group"
                >
                  <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      FOR HOMEOWNERS & INVESTORS
                    </span>
                    <span className="text-sm font-black uppercase text-slate-800 tracking-wide">
                      Residential Plot Listings
                    </span>
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-slate-400 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#006838]"
                  />
                </Link>
                <Link
                  href="/commercial-properties"
                  className="flex-1 border-b-2 border-slate-100 hover:border-[#006838] bg-slate-50 p-4 transition-all duration-300 flex items-center justify-between group"
                >
                  <div>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      FOR COMMERCIAL EXPANSION
                    </span>
                    <span className="text-sm font-black uppercase text-slate-800 tracking-wide">
                      Industrial Land Listings
                    </span>
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-slate-400 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#006838]"
                  />
                </Link>
              </div>
            </div>

            {/* Right Column bullet points (60%) */}
            <div className="lg:col-span-6 bg-slate-50 p-8 border border-slate-100">
              <h5 className="text-[11px] font-black text-[#006838] uppercase tracking-widest mb-6">
                Our platform offers:
              </h5>

              <ul className="space-y-5">
                {[
                  {
                    title: 'Curated Live Inventory',
                    desc: 'A curated, real-time inventory of verified land listings across Dubai and the UAE',
                  },
                  {
                    title: 'Vetted Professional Network',
                    desc: 'A trusted network of over 2,000 registered real estate agents and 200 developers',
                  },
                  {
                    title: 'Secure Interfaces',
                    desc: 'A secure, compliant, and user-friendly transaction interface',
                  },
                  {
                    title: 'Advanced Data Transparency',
                    desc: 'A framework of data transparency, on-demand support, and market insights tailored for professionals.',
                  },
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-4 items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-[#006838]/10 text-[#006838] rounded-full flex items-center justify-center mt-0.5">
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    <div>
                      <strong className="text-slate-800 font-extrabold text-sm uppercase block tracking-wide">
                        {item.title}
                      </strong>
                      <span className="text-slate-600 text-sm leading-relaxed font-medium">
                        {item.desc}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Animated Stats Counter Row */}
      <section className="py-16 bg-slate-50 border-y border-slate-100">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <Counter value={stat1Val} label={stat1Lbl} />
            <Counter value={stat2Val} label={stat2Lbl} />
            <Counter value={stat3Val} label={stat3Lbl} />
            <Counter value={stat4Val} label={stat4Lbl} />
          </div>
        </div>
      </section>

      {/* 6. Vision & Mission Cards */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision Card */}
            <div className="bg-white p-8 md:p-10 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.03)] hover:shadow-[0_15px_35px_-8px_rgba(0,0,0,0.07)] hover:border-slate-200 transition-all duration-300 flex flex-col justify-between group">
              <div className="space-y-6">
                <div className="w-12 h-12 bg-[#006838]/10 text-[#006838] flex items-center justify-center rounded-none transition-transform duration-300 group-hover:scale-110">
                  <Compass size={24} />
                </div>
                <div className="space-y-2">
                  <h3
                    className="text-2xl font-black text-slate-800 uppercase tracking-wide"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {meta?.vision_title || 'Vision'}
                  </h3>
                  <div className="w-8 h-0.5 bg-[#82BC41]"></div>
                </div>
                <p className="text-slate-600 text-[15px] leading-relaxed font-medium">
                  {meta?.vision_desc ||
                    'To become the UAE’s most trusted and intelligent land portal in Dubai for land investments — enabling real estate professionals to build, invest, and innovate with certainty.'}
                </p>
              </div>
            </div>

            {/* Mission Card */}
            <div className="bg-white p-8 md:p-10 border border-slate-100 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.03)] hover:shadow-[0_15px_35px_-8px_rgba(0,0,0,0.07)] hover:border-slate-200 transition-all duration-300 flex flex-col justify-between group">
              <div className="space-y-6">
                <div className="w-12 h-12 bg-[#82BC41]/10 text-[#82BC41] flex items-center justify-center rounded-none transition-transform duration-300 group-hover:scale-110">
                  <Target size={24} />
                </div>
                <div className="space-y-2">
                  <h3
                    className="text-2xl font-black text-slate-800 uppercase tracking-wide"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {meta?.mission_title || 'Mission'}
                  </h3>
                  <div className="w-8 h-0.5 bg-[#006838]"></div>
                </div>
                <p className="text-slate-600 text-[15px] leading-relaxed font-medium">
                  {meta?.mission_desc ||
                    'To establish a transparent, tech-driven ecosystem that connects genuine landowners, verified real estate professionals, and credible developers, transforming how land transactions are conducted in Dubai and the UAE.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Asymmetric Sticky "Our Strategy" Section */}
      <section className="py-20 bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-6 lg:px-12" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Left Sticky Title (33%) */}
            <div className="lg:col-span-4 lg:sticky lg:top-28 h-fit space-y-4">
              <span className="text-[#006838] text-xs font-black uppercase tracking-[0.25em] block">
                Executing Excellence
              </span>
              <h2
                className="text-3xl md:text-4xl font-extrabold text-[#1a1a1a] uppercase"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Our Strategy
              </h2>
              <div className="w-12 h-1 bg-[#82BC41] rounded"></div>
              <p className="text-slate-500 text-sm font-medium leading-relaxed pt-2">
                At PlotAE, we deploy a disciplined operational framework that prioritizes direct
                communication, authentic validation, and streamlined transaction pipelines.
              </p>
            </div>

            {/* Right Strategy Lists (66%) */}
            <div className="lg:col-span-8 space-y-6">
              {[
                {
                  num: '01',
                  title: 'Focus on Land',
                  desc: 'As a trusted land platform in the UAE , we are exclusively dedicated to land and plots, offering unmatched specialization and insights.',
                  icon: <Grid size={18} className="text-[#006838]" />,
                },
                {
                  num: '02',
                  title: 'Verified Listings',
                  desc: 'Every property featured on our verified land listings undergoes a verification process to ensure authenticity and eliminate ambiguity.',
                  icon: <Shield size={18} className="text-[#006838]" />,
                },
                {
                  num: '03',
                  title: 'Professional B2B Network',
                  desc: 'Our platform connects a vetted community of over 2,000 agents and 200 developers to accelerate qualified deal-making.',
                  icon: <Users size={18} className="text-[#006838]" />,
                },
                {
                  num: '04',
                  title: 'Live Inventory',
                  desc: 'Unlike general property portals, our land portal in Dubai ensures listings are regularly updated, giving buyers and brokers real-time visibility into available parcels.',
                  icon: <RefreshCw size={18} className="text-[#006838]" />,
                },
                {
                  num: '05',
                  title: 'Global Investor-Friendly',
                  desc: 'With clear, professional communication and an international-standard interface, PlotAE is optimized for local and global real estate investors alike.',
                  icon: <Globe size={18} className="text-[#006838]" />,
                },
              ].map((strategy, idx) => (
                <div
                  key={idx}
                  className="bg-white p-6 md:p-8 border border-slate-100 hover:border-slate-200 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.02)] hover:shadow-[0_10px_35px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 transition-all duration-300 flex items-start gap-5 group"
                >
                  {/* Left Circle Number Badge */}
                  <span className="flex-shrink-0 w-12 h-12 rounded-none bg-slate-50 text-slate-400 group-hover:bg-[#006838]/10 group-hover:text-[#006838] transition-all duration-300 text-sm font-black flex items-center justify-center">
                    {strategy.num}
                  </span>

                  {/* Strategy Content */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {strategy.icon}
                      <h4
                        className="text-[15px] font-black uppercase text-slate-800 tracking-wide"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                      >
                        {strategy.title}
                      </h4>
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed font-medium">
                      {strategy.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. Testimonials Section */}
      <TestimonialsSection />

      <Footer />
    </div>
  );
}
